/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * MainLoop API — courses, lessons, and enrollments for Python, Vibecoding, and AI classes for middle and high schoolers.
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  AttachmentDownload,
  BroadcastMessageInput,
  CodeSnippet,
  CodeSnippetWithLesson,
  Course,
  CourseDetail,
  CourseSection,
  CreateCheckoutSessionInput,
  CreateCheckoutSessionOutput,
  CreateCourseInput,
  CreateHomeworkInput,
  CreateLessonAttachmentInput,
  CreateLessonInput,
  CreateReadingInput,
  CreateSectionInput,
  DirectMessage,
  DirectMessageWithCourse,
  Enrollment,
  EnrollmentInput,
  EnrollmentWithCourse,
  ErrorResponse,
  FulfillCheckoutOutput,
  GradeSubmissionInput,
  HealthStatus,
  Homework,
  HomeworkSubmission,
  Lesson,
  LessonAttachment,
  LessonDetail,
  LessonPlayback,
  ListCoursesParams,
  ListUpcomingSessionsParams,
  Message,
  MessageWithCourse,
  Reading,
  ReorderInput,
  SaveSnippetInput,
  SectionSession,
  SendDirectMessageInput,
  StatsOverview,
  SubmissionWithContext,
  SubmitHomeworkInput,
  Teacher,
  TeacherCalendarSession,
  UpcomingSession,
  UpdateCourseInput,
  UpdateHomeworkInput,
  UpdateLessonInput,
  UpdateReadingInput,
  UpdateSectionInput,
  UpdateSnippetInput,
  UploadUrlRequest,
  UploadUrlResponse,
  UpsertLessonSessionInput,
} from "./api.schemas";

import { customFetch } from "../custom-fetch";
import type { ErrorType, BodyType } from "../custom-fetch";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * @summary Health check
 */
export const getHealthCheckUrl = () => {
  return `/api/healthz`;
};

export const healthCheck = async (
  options?: RequestInit,
): Promise<HealthStatus> => {
  return customFetch<HealthStatus>(getHealthCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getHealthCheckQueryKey = () => {
  return [`/api/healthz`] as const;
};

export const getHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({
    signal,
  }) => healthCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type HealthCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof healthCheck>>
>;
export type HealthCheckQueryError = ErrorType<unknown>;

/**
 * @summary Health check
 */

export function useHealthCheck<
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List all courses
 */
export const getListCoursesUrl = (params?: ListCoursesParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/courses?${stringifiedParams}`
    : `/api/courses`;
};

export const listCourses = async (
  params?: ListCoursesParams,
  options?: RequestInit,
): Promise<Course[]> => {
  return customFetch<Course[]>(getListCoursesUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListCoursesQueryKey = (params?: ListCoursesParams) => {
  return [`/api/courses`, ...(params ? [params] : [])] as const;
};

export const getListCoursesQueryOptions = <
  TData = Awaited<ReturnType<typeof listCourses>>,
  TError = ErrorType<unknown>,
>(
  params?: ListCoursesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listCourses>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCoursesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCourses>>> = ({
    signal,
  }) => listCourses(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCourses>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListCoursesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listCourses>>
>;
export type ListCoursesQueryError = ErrorType<unknown>;

/**
 * @summary List all courses
 */

export function useListCourses<
  TData = Awaited<ReturnType<typeof listCourses>>,
  TError = ErrorType<unknown>,
>(
  params?: ListCoursesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listCourses>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListCoursesQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get a course by slug, including its lessons (with readings and homework)
 */
export const getGetCourseUrl = (slug: string) => {
  return `/api/courses/${slug}`;
};

export const getCourse = async (
  slug: string,
  options?: RequestInit,
): Promise<CourseDetail> => {
  return customFetch<CourseDetail>(getGetCourseUrl(slug), {
    ...options,
    method: "GET",
  });
};

export const getGetCourseQueryKey = (slug: string) => {
  return [`/api/courses/${slug}`] as const;
};

export const getGetCourseQueryOptions = <
  TData = Awaited<ReturnType<typeof getCourse>>,
  TError = ErrorType<ErrorResponse>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCourse>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCourseQueryKey(slug);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCourse>>> = ({
    signal,
  }) => getCourse(slug, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCourse>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetCourseQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCourse>>
>;
export type GetCourseQueryError = ErrorType<ErrorResponse>;

/**
 * @summary Get a course by slug, including its lessons (with readings and homework)
 */

export function useGetCourse<
  TData = Awaited<ReturnType<typeof getCourse>>,
  TError = ErrorType<ErrorResponse>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCourse>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetCourseQueryOptions(slug, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get a single lesson with its readings, homework, and (for live) zoom info or (for async) video
 */
export const getGetLessonUrl = (id: number) => {
  return `/api/lessons/${id}`;
};

export const getLesson = async (
  id: number,
  options?: RequestInit,
): Promise<LessonDetail> => {
  return customFetch<LessonDetail>(getGetLessonUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetLessonQueryKey = (id: number) => {
  return [`/api/lessons/${id}`] as const;
};

export const getGetLessonQueryOptions = <
  TData = Awaited<ReturnType<typeof getLesson>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getLesson>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLessonQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLesson>>> = ({
    signal,
  }) => getLesson(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getLesson>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetLessonQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLesson>>
>;
export type GetLessonQueryError = ErrorType<ErrorResponse>;

/**
 * @summary Get a single lesson with its readings, homework, and (for live) zoom info or (for async) video
 */

export function useGetLesson<
  TData = Awaited<ReturnType<typeof getLesson>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getLesson>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetLessonQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get a short-lived signed playback URL for an async lesson video (auth required, enrolled students or course teachers only)
 */
export const getGetLessonPlaybackUrl = (id: number) => {
  return `/api/lessons/${id}/playback`;
};

export const getLessonPlayback = async (
  id: number,
  options?: RequestInit,
): Promise<LessonPlayback> => {
  return customFetch<LessonPlayback>(getGetLessonPlaybackUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetLessonPlaybackQueryKey = (id: number) => {
  return [`/api/lessons/${id}/playback`] as const;
};

export const getGetLessonPlaybackQueryOptions = <
  TData = Awaited<ReturnType<typeof getLessonPlayback>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getLessonPlayback>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLessonPlaybackQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getLessonPlayback>>
  > = ({ signal }) => getLessonPlayback(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getLessonPlayback>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetLessonPlaybackQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLessonPlayback>>
>;
export type GetLessonPlaybackQueryError = ErrorType<ErrorResponse>;

/**
 * @summary Get a short-lived signed playback URL for an async lesson video (auth required, enrolled students or course teachers only)
 */

export function useGetLessonPlayback<
  TData = Awaited<ReturnType<typeof getLessonPlayback>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getLessonPlayback>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetLessonPlaybackQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get a short-lived signed download URL for a lesson attachment (enrolled students or course teachers only)
 */
export const getGetLessonAttachmentDownloadUrl = (
  id: number,
  attachmentId: number,
) => {
  return `/api/lessons/${id}/attachments/${attachmentId}/download`;
};

export const getLessonAttachmentDownload = async (
  id: number,
  attachmentId: number,
  options?: RequestInit,
): Promise<AttachmentDownload> => {
  return customFetch<AttachmentDownload>(
    getGetLessonAttachmentDownloadUrl(id, attachmentId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetLessonAttachmentDownloadQueryKey = (
  id: number,
  attachmentId: number,
) => {
  return [`/api/lessons/${id}/attachments/${attachmentId}/download`] as const;
};

export const getGetLessonAttachmentDownloadQueryOptions = <
  TData = Awaited<ReturnType<typeof getLessonAttachmentDownload>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: number,
  attachmentId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getLessonAttachmentDownload>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetLessonAttachmentDownloadQueryKey(id, attachmentId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getLessonAttachmentDownload>>
  > = ({ signal }) =>
    getLessonAttachmentDownload(id, attachmentId, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: !!(id && attachmentId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getLessonAttachmentDownload>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetLessonAttachmentDownloadQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLessonAttachmentDownload>>
>;
export type GetLessonAttachmentDownloadQueryError = ErrorType<ErrorResponse>;

/**
 * @summary Get a short-lived signed download URL for a lesson attachment (enrolled students or course teachers only)
 */

export function useGetLessonAttachmentDownload<
  TData = Awaited<ReturnType<typeof getLessonAttachmentDownload>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: number,
  attachmentId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getLessonAttachmentDownload>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetLessonAttachmentDownloadQueryOptions(
    id,
    attachmentId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Upserts the caller's submission for this homework item. Resubmitting replaces the content and clears any existing grade so the teacher knows to re-review. Students can only ever read or write their own submission.

 * @summary Turn in (or update) your submission for a homework item (enrolled students only)
 */
export const getSubmitHomeworkUrl = (id: number) => {
  return `/api/homework/${id}/submission`;
};

export const submitHomework = async (
  id: number,
  submitHomeworkInput: SubmitHomeworkInput,
  options?: RequestInit,
): Promise<HomeworkSubmission> => {
  return customFetch<HomeworkSubmission>(getSubmitHomeworkUrl(id), {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(submitHomeworkInput),
  });
};

export const getSubmitHomeworkMutationOptions = <
  TError = ErrorType<void | ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof submitHomework>>,
    TError,
    { id: number; data: BodyType<SubmitHomeworkInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof submitHomework>>,
  TError,
  { id: number; data: BodyType<SubmitHomeworkInput> },
  TContext
> => {
  const mutationKey = ["submitHomework"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof submitHomework>>,
    { id: number; data: BodyType<SubmitHomeworkInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return submitHomework(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SubmitHomeworkMutationResult = NonNullable<
  Awaited<ReturnType<typeof submitHomework>>
>;
export type SubmitHomeworkMutationBody = BodyType<SubmitHomeworkInput>;
export type SubmitHomeworkMutationError = ErrorType<void | ErrorResponse>;

/**
 * @summary Turn in (or update) your submission for a homework item (enrolled students only)
 */
export const useSubmitHomework = <
  TError = ErrorType<void | ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof submitHomework>>,
    TError,
    { id: number; data: BodyType<SubmitHomeworkInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof submitHomework>>,
  TError,
  { id: number; data: BodyType<SubmitHomeworkInput> },
  TContext
> => {
  return useMutation(getSubmitHomeworkMutationOptions(options));
};

/**
 * Returns a presigned PUT URL plus the canonical object path to store in your DB.
The client uploads file bytes directly to the returned URL — never to this server.
Objects uploaded here live in the `uploads/` namespace and are NOT publicly readable;
they are accessed through gated endpoints such as `GET /lessons/:id/playback`.
Use this endpoint for lesson videos. For course cover images use `POST /storage/cover-uploads/request-url` instead.

 * @summary Request a presigned URL for direct upload to object storage (teachers only)
 */
export const getRequestUploadUrlUrl = () => {
  return `/api/storage/uploads/request-url`;
};

export const requestUploadUrl = async (
  uploadUrlRequest: UploadUrlRequest,
  options?: RequestInit,
): Promise<UploadUrlResponse> => {
  return customFetch<UploadUrlResponse>(getRequestUploadUrlUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(uploadUrlRequest),
  });
};

export const getRequestUploadUrlMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof requestUploadUrl>>,
    TError,
    { data: BodyType<UploadUrlRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof requestUploadUrl>>,
  TError,
  { data: BodyType<UploadUrlRequest> },
  TContext
> => {
  const mutationKey = ["requestUploadUrl"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof requestUploadUrl>>,
    { data: BodyType<UploadUrlRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return requestUploadUrl(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RequestUploadUrlMutationResult = NonNullable<
  Awaited<ReturnType<typeof requestUploadUrl>>
>;
export type RequestUploadUrlMutationBody = BodyType<UploadUrlRequest>;
export type RequestUploadUrlMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Request a presigned URL for direct upload to object storage (teachers only)
 */
export const useRequestUploadUrl = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof requestUploadUrl>>,
    TError,
    { data: BodyType<UploadUrlRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof requestUploadUrl>>,
  TError,
  { data: BodyType<UploadUrlRequest> },
  TContext
> => {
  return useMutation(getRequestUploadUrlMutationOptions(options));
};

/**
 * Like `POST /storage/uploads/request-url`, but the returned `objectPath` lives in
the `covers/` namespace and is publicly readable through the
`GET /api/objects/covers/:id` passthrough. Use this for course cover images.
Save the URL `/api/objects/covers/<id>` (derived from `objectPath`) on the course.

 * @summary Request a presigned URL for uploading a course cover image (teachers only)
 */
export const getRequestCoverUploadUrlUrl = () => {
  return `/api/storage/cover-uploads/request-url`;
};

export const requestCoverUploadUrl = async (
  uploadUrlRequest: UploadUrlRequest,
  options?: RequestInit,
): Promise<UploadUrlResponse> => {
  return customFetch<UploadUrlResponse>(getRequestCoverUploadUrlUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(uploadUrlRequest),
  });
};

export const getRequestCoverUploadUrlMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof requestCoverUploadUrl>>,
    TError,
    { data: BodyType<UploadUrlRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof requestCoverUploadUrl>>,
  TError,
  { data: BodyType<UploadUrlRequest> },
  TContext
> => {
  const mutationKey = ["requestCoverUploadUrl"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof requestCoverUploadUrl>>,
    { data: BodyType<UploadUrlRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return requestCoverUploadUrl(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RequestCoverUploadUrlMutationResult = NonNullable<
  Awaited<ReturnType<typeof requestCoverUploadUrl>>
>;
export type RequestCoverUploadUrlMutationBody = BodyType<UploadUrlRequest>;
export type RequestCoverUploadUrlMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Request a presigned URL for uploading a course cover image (teachers only)
 */
export const useRequestCoverUploadUrl = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof requestCoverUploadUrl>>,
    TError,
    { data: BodyType<UploadUrlRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof requestCoverUploadUrl>>,
  TError,
  { data: BodyType<UploadUrlRequest> },
  TContext
> => {
  return useMutation(getRequestCoverUploadUrlMutationOptions(options));
};

/**
 * Free-course self-enroll path. Paid courses must use Stripe Checkout
via `POST /checkout/sessions`. The body's `studentEmail` must match
the caller's primary Clerk email; the server uses the caller's email
rather than trusting the body. Draft and unpublished courses always
return 404.

 * @summary Self-enroll the signed-in user in a free, published course (auth required)
 */
export const getCreateEnrollmentUrl = () => {
  return `/api/enrollments`;
};

export const createEnrollment = async (
  enrollmentInput: EnrollmentInput,
  options?: RequestInit,
): Promise<Enrollment> => {
  return customFetch<Enrollment>(getCreateEnrollmentUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(enrollmentInput),
  });
};

export const getCreateEnrollmentMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createEnrollment>>,
    TError,
    { data: BodyType<EnrollmentInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createEnrollment>>,
  TError,
  { data: BodyType<EnrollmentInput> },
  TContext
> => {
  const mutationKey = ["createEnrollment"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createEnrollment>>,
    { data: BodyType<EnrollmentInput> }
  > = (props) => {
    const { data } = props ?? {};

    return createEnrollment(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateEnrollmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof createEnrollment>>
>;
export type CreateEnrollmentMutationBody = BodyType<EnrollmentInput>;
export type CreateEnrollmentMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Self-enroll the signed-in user in a free, published course (auth required)
 */
export const useCreateEnrollment = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createEnrollment>>,
    TError,
    { data: BodyType<EnrollmentInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createEnrollment>>,
  TError,
  { data: BodyType<EnrollmentInput> },
  TContext
> => {
  return useMutation(getCreateEnrollmentMutationOptions(options));
};

/**
 * @summary List the signed-in user's enrollments (matched on their primary email, auth required)
 */
export const getListMyEnrollmentsUrl = () => {
  return `/api/enrollments/me`;
};

export const listMyEnrollments = async (
  options?: RequestInit,
): Promise<EnrollmentWithCourse[]> => {
  return customFetch<EnrollmentWithCourse[]>(getListMyEnrollmentsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListMyEnrollmentsQueryKey = () => {
  return [`/api/enrollments/me`] as const;
};

export const getListMyEnrollmentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyEnrollments>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyEnrollments>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMyEnrollmentsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyEnrollments>>
  > = ({ signal }) => listMyEnrollments({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyEnrollments>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyEnrollmentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyEnrollments>>
>;
export type ListMyEnrollmentsQueryError = ErrorType<ErrorResponse>;

/**
 * @summary List the signed-in user's enrollments (matched on their primary email, auth required)
 */

export function useListMyEnrollments<
  TData = Awaited<ReturnType<typeof listMyEnrollments>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyEnrollments>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyEnrollmentsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Always regenerated from the latest lesson data — no caching. Each VEVENT
uses a stable UID per lesson so re-importing the same file updates the
existing event in the user's calendar instead of creating a duplicate.
Calendar app subscription URLs (Google "from URL", Apple "Subscribe") are
not supported in v1 because the endpoint requires the user's session.

 * @summary Download an iCalendar (.ics) feed of upcoming live sessions for an enrollment (auth required, owner only)
 */
export const getGetEnrollmentCalendarUrl = (id: number) => {
  return `/api/enrollments/${id}/calendar.ics`;
};

export const getEnrollmentCalendar = async (
  id: number,
  options?: RequestInit,
): Promise<string> => {
  return customFetch<string>(getGetEnrollmentCalendarUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetEnrollmentCalendarQueryKey = (id: number) => {
  return [`/api/enrollments/${id}/calendar.ics`] as const;
};

export const getGetEnrollmentCalendarQueryOptions = <
  TData = Awaited<ReturnType<typeof getEnrollmentCalendar>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getEnrollmentCalendar>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetEnrollmentCalendarQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getEnrollmentCalendar>>
  > = ({ signal }) => getEnrollmentCalendar(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getEnrollmentCalendar>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetEnrollmentCalendarQueryResult = NonNullable<
  Awaited<ReturnType<typeof getEnrollmentCalendar>>
>;
export type GetEnrollmentCalendarQueryError = ErrorType<ErrorResponse>;

/**
 * @summary Download an iCalendar (.ics) feed of upcoming live sessions for an enrollment (auth required, owner only)
 */

export function useGetEnrollmentCalendar<
  TData = Awaited<ReturnType<typeof getEnrollmentCalendar>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getEnrollmentCalendar>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetEnrollmentCalendarQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a Stripe Checkout Session for a course (auth required)
 */
export const getCreateCheckoutSessionUrl = () => {
  return `/api/checkout/sessions`;
};

export const createCheckoutSession = async (
  createCheckoutSessionInput: CreateCheckoutSessionInput,
  options?: RequestInit,
): Promise<CreateCheckoutSessionOutput> => {
  return customFetch<CreateCheckoutSessionOutput>(
    getCreateCheckoutSessionUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(createCheckoutSessionInput),
    },
  );
};

export const getCreateCheckoutSessionMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCheckoutSession>>,
    TError,
    { data: BodyType<CreateCheckoutSessionInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createCheckoutSession>>,
  TError,
  { data: BodyType<CreateCheckoutSessionInput> },
  TContext
> => {
  const mutationKey = ["createCheckoutSession"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createCheckoutSession>>,
    { data: BodyType<CreateCheckoutSessionInput> }
  > = (props) => {
    const { data } = props ?? {};

    return createCheckoutSession(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCheckoutSessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createCheckoutSession>>
>;
export type CreateCheckoutSessionMutationBody =
  BodyType<CreateCheckoutSessionInput>;
export type CreateCheckoutSessionMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Create a Stripe Checkout Session for a course (auth required)
 */
export const useCreateCheckoutSession = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCheckoutSession>>,
    TError,
    { data: BodyType<CreateCheckoutSessionInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createCheckoutSession>>,
  TError,
  { data: BodyType<CreateCheckoutSessionInput> },
  TContext
> => {
  return useMutation(getCreateCheckoutSessionMutationOptions(options));
};

/**
 * @summary Verify a paid Stripe Checkout Session and create the enrollment (auth required, idempotent)
 */
export const getFulfillCheckoutSessionUrl = (id: string) => {
  return `/api/checkout/sessions/${id}/fulfill`;
};

export const fulfillCheckoutSession = async (
  id: string,
  options?: RequestInit,
): Promise<FulfillCheckoutOutput> => {
  return customFetch<FulfillCheckoutOutput>(getFulfillCheckoutSessionUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getFulfillCheckoutSessionMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof fulfillCheckoutSession>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof fulfillCheckoutSession>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["fulfillCheckoutSession"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof fulfillCheckoutSession>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return fulfillCheckoutSession(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type FulfillCheckoutSessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof fulfillCheckoutSession>>
>;

export type FulfillCheckoutSessionMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Verify a paid Stripe Checkout Session and create the enrollment (auth required, idempotent)
 */
export const useFulfillCheckoutSession = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof fulfillCheckoutSession>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof fulfillCheckoutSession>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getFulfillCheckoutSessionMutationOptions(options));
};

/**
 * @summary Create or update a saved snippet (auth required). When lessonId is set, upserts the user's snippet for that lesson.
 */
export const getSaveSnippetUrl = () => {
  return `/api/snippets`;
};

export const saveSnippet = async (
  saveSnippetInput: SaveSnippetInput,
  options?: RequestInit,
): Promise<CodeSnippet> => {
  return customFetch<CodeSnippet>(getSaveSnippetUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(saveSnippetInput),
  });
};

export const getSaveSnippetMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof saveSnippet>>,
    TError,
    { data: BodyType<SaveSnippetInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof saveSnippet>>,
  TError,
  { data: BodyType<SaveSnippetInput> },
  TContext
> => {
  const mutationKey = ["saveSnippet"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof saveSnippet>>,
    { data: BodyType<SaveSnippetInput> }
  > = (props) => {
    const { data } = props ?? {};

    return saveSnippet(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SaveSnippetMutationResult = NonNullable<
  Awaited<ReturnType<typeof saveSnippet>>
>;
export type SaveSnippetMutationBody = BodyType<SaveSnippetInput>;
export type SaveSnippetMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Create or update a saved snippet (auth required). When lessonId is set, upserts the user's snippet for that lesson.
 */
export const useSaveSnippet = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof saveSnippet>>,
    TError,
    { data: BodyType<SaveSnippetInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof saveSnippet>>,
  TError,
  { data: BodyType<SaveSnippetInput> },
  TContext
> => {
  return useMutation(getSaveSnippetMutationOptions(options));
};

/**
 * @summary List the signed-in user's saved snippets (auth required)
 */
export const getListMySnippetsUrl = () => {
  return `/api/snippets/me`;
};

export const listMySnippets = async (
  options?: RequestInit,
): Promise<CodeSnippetWithLesson[]> => {
  return customFetch<CodeSnippetWithLesson[]>(getListMySnippetsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListMySnippetsQueryKey = () => {
  return [`/api/snippets/me`] as const;
};

export const getListMySnippetsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMySnippets>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMySnippets>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMySnippetsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listMySnippets>>> = ({
    signal,
  }) => listMySnippets({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listMySnippets>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMySnippetsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMySnippets>>
>;
export type ListMySnippetsQueryError = ErrorType<ErrorResponse>;

/**
 * @summary List the signed-in user's saved snippets (auth required)
 */

export function useListMySnippets<
  TData = Awaited<ReturnType<typeof listMySnippets>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMySnippets>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMySnippetsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get a snippet by id (public, read-only share link)
 */
export const getGetSnippetUrl = (id: string) => {
  return `/api/snippets/${id}`;
};

export const getSnippet = async (
  id: string,
  options?: RequestInit,
): Promise<CodeSnippetWithLesson> => {
  return customFetch<CodeSnippetWithLesson>(getGetSnippetUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetSnippetQueryKey = (id: string) => {
  return [`/api/snippets/${id}`] as const;
};

export const getGetSnippetQueryOptions = <
  TData = Awaited<ReturnType<typeof getSnippet>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getSnippet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSnippetQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSnippet>>> = ({
    signal,
  }) => getSnippet(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getSnippet>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetSnippetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSnippet>>
>;
export type GetSnippetQueryError = ErrorType<ErrorResponse>;

/**
 * @summary Get a snippet by id (public, read-only share link)
 */

export function useGetSnippet<
  TData = Awaited<ReturnType<typeof getSnippet>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getSnippet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetSnippetQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Delete a snippet you own (auth required, owner only)
 */
export const getDeleteSnippetUrl = (id: string) => {
  return `/api/snippets/${id}`;
};

export const deleteSnippet = async (
  id: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteSnippetUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteSnippetMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSnippet>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteSnippet>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["deleteSnippet"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteSnippet>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteSnippet(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteSnippetMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteSnippet>>
>;

export type DeleteSnippetMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Delete a snippet you own (auth required, owner only)
 */
export const useDeleteSnippet = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSnippet>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteSnippet>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getDeleteSnippetMutationOptions(options));
};

/**
 * @summary Update editable fields on a snippet you own (auth required, owner only)
 */
export const getUpdateSnippetUrl = (id: string) => {
  return `/api/snippets/${id}`;
};

export const updateSnippet = async (
  id: string,
  updateSnippetInput: UpdateSnippetInput,
  options?: RequestInit,
): Promise<CodeSnippet> => {
  return customFetch<CodeSnippet>(getUpdateSnippetUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateSnippetInput),
  });
};

export const getUpdateSnippetMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateSnippet>>,
    TError,
    { id: string; data: BodyType<UpdateSnippetInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateSnippet>>,
  TError,
  { id: string; data: BodyType<UpdateSnippetInput> },
  TContext
> => {
  const mutationKey = ["updateSnippet"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateSnippet>>,
    { id: string; data: BodyType<UpdateSnippetInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateSnippet(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateSnippetMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateSnippet>>
>;
export type UpdateSnippetMutationBody = BodyType<UpdateSnippetInput>;
export type UpdateSnippetMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Update editable fields on a snippet you own (auth required, owner only)
 */
export const useUpdateSnippet = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateSnippet>>,
    TError,
    { id: string; data: BodyType<UpdateSnippetInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateSnippet>>,
  TError,
  { id: string; data: BodyType<UpdateSnippetInput> },
  TContext
> => {
  return useMutation(getUpdateSnippetMutationOptions(options));
};

/**
 * @summary Fork a snippet into the signed-in user's account (auth required)
 */
export const getForkSnippetUrl = (id: string) => {
  return `/api/snippets/${id}/fork`;
};

export const forkSnippet = async (
  id: string,
  options?: RequestInit,
): Promise<CodeSnippet> => {
  return customFetch<CodeSnippet>(getForkSnippetUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getForkSnippetMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof forkSnippet>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof forkSnippet>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["forkSnippet"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof forkSnippet>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return forkSnippet(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ForkSnippetMutationResult = NonNullable<
  Awaited<ReturnType<typeof forkSnippet>>
>;

export type ForkSnippetMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Fork a snippet into the signed-in user's account (auth required)
 */
export const useForkSnippet = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof forkSnippet>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof forkSnippet>>,
  TError,
  { id: string },
  TContext
> => {
  return useMutation(getForkSnippetMutationOptions(options));
};

/**
 * @summary Get the signed-in user's teacher profile (auth required)
 */
export const getGetMyTeacherProfileUrl = () => {
  return `/api/teacher/me`;
};

export const getMyTeacherProfile = async (
  options?: RequestInit,
): Promise<Teacher> => {
  return customFetch<Teacher>(getGetMyTeacherProfileUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetMyTeacherProfileQueryKey = () => {
  return [`/api/teacher/me`] as const;
};

export const getGetMyTeacherProfileQueryOptions = <
  TData = Awaited<ReturnType<typeof getMyTeacherProfile>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getMyTeacherProfile>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMyTeacherProfileQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMyTeacherProfile>>
  > = ({ signal }) => getMyTeacherProfile({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMyTeacherProfile>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetMyTeacherProfileQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMyTeacherProfile>>
>;
export type GetMyTeacherProfileQueryError = ErrorType<ErrorResponse>;

/**
 * @summary Get the signed-in user's teacher profile (auth required)
 */

export function useGetMyTeacherProfile<
  TData = Awaited<ReturnType<typeof getMyTeacherProfile>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getMyTeacherProfile>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetMyTeacherProfileQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List courses owned by the signed-in teacher
 */
export const getListMyTeacherCoursesUrl = () => {
  return `/api/teacher/courses`;
};

export const listMyTeacherCourses = async (
  options?: RequestInit,
): Promise<Course[]> => {
  return customFetch<Course[]>(getListMyTeacherCoursesUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListMyTeacherCoursesQueryKey = () => {
  return [`/api/teacher/courses`] as const;
};

export const getListMyTeacherCoursesQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyTeacherCourses>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyTeacherCourses>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMyTeacherCoursesQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyTeacherCourses>>
  > = ({ signal }) => listMyTeacherCourses({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyTeacherCourses>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyTeacherCoursesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyTeacherCourses>>
>;
export type ListMyTeacherCoursesQueryError = ErrorType<void>;

/**
 * @summary List courses owned by the signed-in teacher
 */

export function useListMyTeacherCourses<
  TData = Awaited<ReturnType<typeof listMyTeacherCourses>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyTeacherCourses>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyTeacherCoursesQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a new course owned by the signed-in teacher
 */
export const getCreateMyCourseUrl = () => {
  return `/api/teacher/courses`;
};

export const createMyCourse = async (
  createCourseInput: CreateCourseInput,
  options?: RequestInit,
): Promise<Course> => {
  return customFetch<Course>(getCreateMyCourseUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createCourseInput),
  });
};

export const getCreateMyCourseMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyCourse>>,
    TError,
    { data: BodyType<CreateCourseInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMyCourse>>,
  TError,
  { data: BodyType<CreateCourseInput> },
  TContext
> => {
  const mutationKey = ["createMyCourse"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMyCourse>>,
    { data: BodyType<CreateCourseInput> }
  > = (props) => {
    const { data } = props ?? {};

    return createMyCourse(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMyCourseMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMyCourse>>
>;
export type CreateMyCourseMutationBody = BodyType<CreateCourseInput>;
export type CreateMyCourseMutationError = ErrorType<void>;

/**
 * @summary Create a new course owned by the signed-in teacher
 */
export const useCreateMyCourse = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyCourse>>,
    TError,
    { data: BodyType<CreateCourseInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createMyCourse>>,
  TError,
  { data: BodyType<CreateCourseInput> },
  TContext
> => {
  return useMutation(getCreateMyCourseMutationOptions(options));
};

/**
 * Returns every scheduled lesson session (one per (section, lesson) with a zoomScheduledAt set) across all courses the teacher owns or co-teaches, ordered by start time. Powers the teacher master calendar.

 * @summary All scheduled live sessions across the signed-in teacher's courses
 */
export const getGetMyTeacherCalendarUrl = () => {
  return `/api/teacher/calendar`;
};

export const getMyTeacherCalendar = async (
  options?: RequestInit,
): Promise<TeacherCalendarSession[]> => {
  return customFetch<TeacherCalendarSession[]>(getGetMyTeacherCalendarUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetMyTeacherCalendarQueryKey = () => {
  return [`/api/teacher/calendar`] as const;
};

export const getGetMyTeacherCalendarQueryOptions = <
  TData = Awaited<ReturnType<typeof getMyTeacherCalendar>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getMyTeacherCalendar>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMyTeacherCalendarQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMyTeacherCalendar>>
  > = ({ signal }) => getMyTeacherCalendar({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMyTeacherCalendar>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetMyTeacherCalendarQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMyTeacherCalendar>>
>;
export type GetMyTeacherCalendarQueryError = ErrorType<void>;

/**
 * @summary All scheduled live sessions across the signed-in teacher's courses
 */

export function useGetMyTeacherCalendar<
  TData = Awaited<ReturnType<typeof getMyTeacherCalendar>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getMyTeacherCalendar>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetMyTeacherCalendarQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Multiple teachers can co-teach a course. Idempotent — calling on a course you already teach returns the course unchanged.
 * @summary Add yourself as a teacher of a course (teacher account required)
 */
export const getClaimCourseUrl = (slug: string) => {
  return `/api/teacher/courses/${slug}/claim`;
};

export const claimCourse = async (
  slug: string,
  options?: RequestInit,
): Promise<Course> => {
  return customFetch<Course>(getClaimCourseUrl(slug), {
    ...options,
    method: "POST",
  });
};

export const getClaimCourseMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof claimCourse>>,
    TError,
    { slug: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof claimCourse>>,
  TError,
  { slug: string },
  TContext
> => {
  const mutationKey = ["claimCourse"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof claimCourse>>,
    { slug: string }
  > = (props) => {
    const { slug } = props ?? {};

    return claimCourse(slug, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ClaimCourseMutationResult = NonNullable<
  Awaited<ReturnType<typeof claimCourse>>
>;

export type ClaimCourseMutationError = ErrorType<void>;

/**
 * @summary Add yourself as a teacher of a course (teacher account required)
 */
export const useClaimCourse = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof claimCourse>>,
    TError,
    { slug: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof claimCourse>>,
  TError,
  { slug: string },
  TContext
> => {
  return useMutation(getClaimCourseMutationOptions(options));
};

/**
 * @summary Update fields on a course you own
 */
export const getUpdateMyCourseUrl = (slug: string) => {
  return `/api/teacher/courses/${slug}`;
};

export const updateMyCourse = async (
  slug: string,
  updateCourseInput: UpdateCourseInput,
  options?: RequestInit,
): Promise<Course> => {
  return customFetch<Course>(getUpdateMyCourseUrl(slug), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateCourseInput),
  });
};

export const getUpdateMyCourseMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMyCourse>>,
    TError,
    { slug: string; data: BodyType<UpdateCourseInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMyCourse>>,
  TError,
  { slug: string; data: BodyType<UpdateCourseInput> },
  TContext
> => {
  const mutationKey = ["updateMyCourse"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMyCourse>>,
    { slug: string; data: BodyType<UpdateCourseInput> }
  > = (props) => {
    const { slug, data } = props ?? {};

    return updateMyCourse(slug, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMyCourseMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMyCourse>>
>;
export type UpdateMyCourseMutationBody = BodyType<UpdateCourseInput>;
export type UpdateMyCourseMutationError = ErrorType<void>;

/**
 * @summary Update fields on a course you own
 */
export const useUpdateMyCourse = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMyCourse>>,
    TError,
    { slug: string; data: BodyType<UpdateCourseInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateMyCourse>>,
  TError,
  { slug: string; data: BodyType<UpdateCourseInput> },
  TContext
> => {
  return useMutation(getUpdateMyCourseMutationOptions(options));
};

/**
 * @summary Permanently delete a course you own (cascades to lessons, enrollments, and messages)
 */
export const getDeleteMyCourseUrl = (slug: string) => {
  return `/api/teacher/courses/${slug}`;
};

export const deleteMyCourse = async (
  slug: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteMyCourseUrl(slug), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteMyCourseMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyCourse>>,
    TError,
    { slug: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteMyCourse>>,
  TError,
  { slug: string },
  TContext
> => {
  const mutationKey = ["deleteMyCourse"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteMyCourse>>,
    { slug: string }
  > = (props) => {
    const { slug } = props ?? {};

    return deleteMyCourse(slug, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteMyCourseMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteMyCourse>>
>;

export type DeleteMyCourseMutationError = ErrorType<void>;

/**
 * @summary Permanently delete a course you own (cascades to lessons, enrollments, and messages)
 */
export const useDeleteMyCourse = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyCourse>>,
    TError,
    { slug: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteMyCourse>>,
  TError,
  { slug: string },
  TContext
> => {
  return useMutation(getDeleteMyCourseMutationOptions(options));
};

/**
 * @summary List enrollments for a course you own
 */
export const getListMyCourseStudentsUrl = (slug: string) => {
  return `/api/teacher/courses/${slug}/students`;
};

export const listMyCourseStudents = async (
  slug: string,
  options?: RequestInit,
): Promise<Enrollment[]> => {
  return customFetch<Enrollment[]>(getListMyCourseStudentsUrl(slug), {
    ...options,
    method: "GET",
  });
};

export const getListMyCourseStudentsQueryKey = (slug: string) => {
  return [`/api/teacher/courses/${slug}/students`] as const;
};

export const getListMyCourseStudentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyCourseStudents>>,
  TError = ErrorType<void>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyCourseStudents>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListMyCourseStudentsQueryKey(slug);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyCourseStudents>>
  > = ({ signal }) => listMyCourseStudents(slug, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyCourseStudents>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyCourseStudentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyCourseStudents>>
>;
export type ListMyCourseStudentsQueryError = ErrorType<void>;

/**
 * @summary List enrollments for a course you own
 */

export function useListMyCourseStudents<
  TData = Awaited<ReturnType<typeof listMyCourseStudents>>,
  TError = ErrorType<void>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyCourseStudents>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyCourseStudentsQueryOptions(slug, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List the saved code snippets for one student in a course you teach
 */
export const getListMyStudentSnippetsUrl = (
  slug: string,
  enrollmentId: number,
) => {
  return `/api/teacher/courses/${slug}/students/${enrollmentId}/snippets`;
};

export const listMyStudentSnippets = async (
  slug: string,
  enrollmentId: number,
  options?: RequestInit,
): Promise<CodeSnippetWithLesson[]> => {
  return customFetch<CodeSnippetWithLesson[]>(
    getListMyStudentSnippetsUrl(slug, enrollmentId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListMyStudentSnippetsQueryKey = (
  slug: string,
  enrollmentId: number,
) => {
  return [
    `/api/teacher/courses/${slug}/students/${enrollmentId}/snippets`,
  ] as const;
};

export const getListMyStudentSnippetsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyStudentSnippets>>,
  TError = ErrorType<void>,
>(
  slug: string,
  enrollmentId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyStudentSnippets>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListMyStudentSnippetsQueryKey(slug, enrollmentId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyStudentSnippets>>
  > = ({ signal }) =>
    listMyStudentSnippets(slug, enrollmentId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!(slug && enrollmentId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyStudentSnippets>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyStudentSnippetsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyStudentSnippets>>
>;
export type ListMyStudentSnippetsQueryError = ErrorType<void>;

/**
 * @summary List the saved code snippets for one student in a course you teach
 */

export function useListMyStudentSnippets<
  TData = Awaited<ReturnType<typeof listMyStudentSnippets>>,
  TError = ErrorType<void>,
>(
  slug: string,
  enrollmentId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyStudentSnippets>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyStudentSnippetsQueryOptions(
    slug,
    enrollmentId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List the homework submissions from one student in a course you teach
 */
export const getListMyStudentSubmissionsUrl = (
  slug: string,
  enrollmentId: number,
) => {
  return `/api/teacher/courses/${slug}/students/${enrollmentId}/submissions`;
};

export const listMyStudentSubmissions = async (
  slug: string,
  enrollmentId: number,
  options?: RequestInit,
): Promise<SubmissionWithContext[]> => {
  return customFetch<SubmissionWithContext[]>(
    getListMyStudentSubmissionsUrl(slug, enrollmentId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListMyStudentSubmissionsQueryKey = (
  slug: string,
  enrollmentId: number,
) => {
  return [
    `/api/teacher/courses/${slug}/students/${enrollmentId}/submissions`,
  ] as const;
};

export const getListMyStudentSubmissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyStudentSubmissions>>,
  TError = ErrorType<void>,
>(
  slug: string,
  enrollmentId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyStudentSubmissions>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListMyStudentSubmissionsQueryKey(slug, enrollmentId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyStudentSubmissions>>
  > = ({ signal }) =>
    listMyStudentSubmissions(slug, enrollmentId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!(slug && enrollmentId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyStudentSubmissions>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyStudentSubmissionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyStudentSubmissions>>
>;
export type ListMyStudentSubmissionsQueryError = ErrorType<void>;

/**
 * @summary List the homework submissions from one student in a course you teach
 */

export function useListMyStudentSubmissions<
  TData = Awaited<ReturnType<typeof listMyStudentSubmissions>>,
  TError = ErrorType<void>,
>(
  slug: string,
  enrollmentId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyStudentSubmissions>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyStudentSubmissionsQueryOptions(
    slug,
    enrollmentId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Set the grade and feedback on a student's homework submission
 */
export const getGradeSubmissionUrl = (id: number) => {
  return `/api/teacher/submissions/${id}/grade`;
};

export const gradeSubmission = async (
  id: number,
  gradeSubmissionInput: GradeSubmissionInput,
  options?: RequestInit,
): Promise<SubmissionWithContext> => {
  return customFetch<SubmissionWithContext>(getGradeSubmissionUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(gradeSubmissionInput),
  });
};

export const getGradeSubmissionMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof gradeSubmission>>,
    TError,
    { id: number; data: BodyType<GradeSubmissionInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof gradeSubmission>>,
  TError,
  { id: number; data: BodyType<GradeSubmissionInput> },
  TContext
> => {
  const mutationKey = ["gradeSubmission"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof gradeSubmission>>,
    { id: number; data: BodyType<GradeSubmissionInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return gradeSubmission(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type GradeSubmissionMutationResult = NonNullable<
  Awaited<ReturnType<typeof gradeSubmission>>
>;
export type GradeSubmissionMutationBody = BodyType<GradeSubmissionInput>;
export type GradeSubmissionMutationError = ErrorType<void>;

/**
 * @summary Set the grade and feedback on a student's homework submission
 */
export const useGradeSubmission = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof gradeSubmission>>,
    TError,
    { id: number; data: BodyType<GradeSubmissionInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof gradeSubmission>>,
  TError,
  { id: number; data: BodyType<GradeSubmissionInput> },
  TContext
> => {
  return useMutation(getGradeSubmissionMutationOptions(options));
};

/**
 * @summary List downloadable attachments on a lesson you teach
 */
export const getListMyLessonAttachmentsUrl = (id: number) => {
  return `/api/teacher/lessons/${id}/attachments`;
};

export const listMyLessonAttachments = async (
  id: number,
  options?: RequestInit,
): Promise<LessonAttachment[]> => {
  return customFetch<LessonAttachment[]>(getListMyLessonAttachmentsUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getListMyLessonAttachmentsQueryKey = (id: number) => {
  return [`/api/teacher/lessons/${id}/attachments`] as const;
};

export const getListMyLessonAttachmentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyLessonAttachments>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyLessonAttachments>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListMyLessonAttachmentsQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyLessonAttachments>>
  > = ({ signal }) =>
    listMyLessonAttachments(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyLessonAttachments>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyLessonAttachmentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyLessonAttachments>>
>;
export type ListMyLessonAttachmentsQueryError = ErrorType<void>;

/**
 * @summary List downloadable attachments on a lesson you teach
 */

export function useListMyLessonAttachments<
  TData = Awaited<ReturnType<typeof listMyLessonAttachments>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyLessonAttachments>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyLessonAttachmentsQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Upload the file first via POST /storage/uploads/request-url, then register it here with the returned objectPath. Students download it through the enrollment-gated GET /lessons/{id}/attachments/{attachmentId}/download.

 * @summary Attach an uploaded file to a lesson you teach
 */
export const getCreateMyLessonAttachmentUrl = (id: number) => {
  return `/api/teacher/lessons/${id}/attachments`;
};

export const createMyLessonAttachment = async (
  id: number,
  createLessonAttachmentInput: CreateLessonAttachmentInput,
  options?: RequestInit,
): Promise<LessonAttachment> => {
  return customFetch<LessonAttachment>(getCreateMyLessonAttachmentUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createLessonAttachmentInput),
  });
};

export const getCreateMyLessonAttachmentMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyLessonAttachment>>,
    TError,
    { id: number; data: BodyType<CreateLessonAttachmentInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMyLessonAttachment>>,
  TError,
  { id: number; data: BodyType<CreateLessonAttachmentInput> },
  TContext
> => {
  const mutationKey = ["createMyLessonAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMyLessonAttachment>>,
    { id: number; data: BodyType<CreateLessonAttachmentInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return createMyLessonAttachment(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMyLessonAttachmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMyLessonAttachment>>
>;
export type CreateMyLessonAttachmentMutationBody =
  BodyType<CreateLessonAttachmentInput>;
export type CreateMyLessonAttachmentMutationError = ErrorType<void>;

/**
 * @summary Attach an uploaded file to a lesson you teach
 */
export const useCreateMyLessonAttachment = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyLessonAttachment>>,
    TError,
    { id: number; data: BodyType<CreateLessonAttachmentInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createMyLessonAttachment>>,
  TError,
  { id: number; data: BodyType<CreateLessonAttachmentInput> },
  TContext
> => {
  return useMutation(getCreateMyLessonAttachmentMutationOptions(options));
};

/**
 * @summary Remove an attachment from a lesson you teach
 */
export const getDeleteMyLessonAttachmentUrl = (id: number) => {
  return `/api/teacher/attachments/${id}`;
};

export const deleteMyLessonAttachment = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteMyLessonAttachmentUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteMyLessonAttachmentMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyLessonAttachment>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteMyLessonAttachment>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteMyLessonAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteMyLessonAttachment>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteMyLessonAttachment(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteMyLessonAttachmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteMyLessonAttachment>>
>;

export type DeleteMyLessonAttachmentMutationError = ErrorType<void>;

/**
 * @summary Remove an attachment from a lesson you teach
 */
export const useDeleteMyLessonAttachment = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyLessonAttachment>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteMyLessonAttachment>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteMyLessonAttachmentMutationOptions(options));
};

/**
 * @summary List direct messages sent to one student in a course you teach
 */
export const getListMyStudentDirectMessagesUrl = (
  slug: string,
  enrollmentId: number,
) => {
  return `/api/teacher/courses/${slug}/students/${enrollmentId}/messages`;
};

export const listMyStudentDirectMessages = async (
  slug: string,
  enrollmentId: number,
  options?: RequestInit,
): Promise<DirectMessage[]> => {
  return customFetch<DirectMessage[]>(
    getListMyStudentDirectMessagesUrl(slug, enrollmentId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListMyStudentDirectMessagesQueryKey = (
  slug: string,
  enrollmentId: number,
) => {
  return [
    `/api/teacher/courses/${slug}/students/${enrollmentId}/messages`,
  ] as const;
};

export const getListMyStudentDirectMessagesQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyStudentDirectMessages>>,
  TError = ErrorType<void>,
>(
  slug: string,
  enrollmentId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyStudentDirectMessages>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListMyStudentDirectMessagesQueryKey(slug, enrollmentId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyStudentDirectMessages>>
  > = ({ signal }) =>
    listMyStudentDirectMessages(slug, enrollmentId, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: !!(slug && enrollmentId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyStudentDirectMessages>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyStudentDirectMessagesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyStudentDirectMessages>>
>;
export type ListMyStudentDirectMessagesQueryError = ErrorType<void>;

/**
 * @summary List direct messages sent to one student in a course you teach
 */

export function useListMyStudentDirectMessages<
  TData = Awaited<ReturnType<typeof listMyStudentDirectMessages>>,
  TError = ErrorType<void>,
>(
  slug: string,
  enrollmentId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyStudentDirectMessages>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyStudentDirectMessagesQueryOptions(
    slug,
    enrollmentId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Send a direct message to one student in a course you teach
 */
export const getSendStudentDirectMessageUrl = (
  slug: string,
  enrollmentId: number,
) => {
  return `/api/teacher/courses/${slug}/students/${enrollmentId}/messages`;
};

export const sendStudentDirectMessage = async (
  slug: string,
  enrollmentId: number,
  sendDirectMessageInput: SendDirectMessageInput,
  options?: RequestInit,
): Promise<DirectMessage> => {
  return customFetch<DirectMessage>(
    getSendStudentDirectMessageUrl(slug, enrollmentId),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(sendDirectMessageInput),
    },
  );
};

export const getSendStudentDirectMessageMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sendStudentDirectMessage>>,
    TError,
    {
      slug: string;
      enrollmentId: number;
      data: BodyType<SendDirectMessageInput>;
    },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sendStudentDirectMessage>>,
  TError,
  {
    slug: string;
    enrollmentId: number;
    data: BodyType<SendDirectMessageInput>;
  },
  TContext
> => {
  const mutationKey = ["sendStudentDirectMessage"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sendStudentDirectMessage>>,
    {
      slug: string;
      enrollmentId: number;
      data: BodyType<SendDirectMessageInput>;
    }
  > = (props) => {
    const { slug, enrollmentId, data } = props ?? {};

    return sendStudentDirectMessage(slug, enrollmentId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SendStudentDirectMessageMutationResult = NonNullable<
  Awaited<ReturnType<typeof sendStudentDirectMessage>>
>;
export type SendStudentDirectMessageMutationBody =
  BodyType<SendDirectMessageInput>;
export type SendStudentDirectMessageMutationError = ErrorType<void>;

/**
 * @summary Send a direct message to one student in a course you teach
 */
export const useSendStudentDirectMessage = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sendStudentDirectMessage>>,
    TError,
    {
      slug: string;
      enrollmentId: number;
      data: BodyType<SendDirectMessageInput>;
    },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof sendStudentDirectMessage>>,
  TError,
  {
    slug: string;
    enrollmentId: number;
    data: BodyType<SendDirectMessageInput>;
  },
  TContext
> => {
  return useMutation(getSendStudentDirectMessageMutationOptions(options));
};

/**
 * @summary List messages you sent to a course you own
 */
export const getListMyCourseMessagesUrl = (slug: string) => {
  return `/api/teacher/courses/${slug}/messages`;
};

export const listMyCourseMessages = async (
  slug: string,
  options?: RequestInit,
): Promise<Message[]> => {
  return customFetch<Message[]>(getListMyCourseMessagesUrl(slug), {
    ...options,
    method: "GET",
  });
};

export const getListMyCourseMessagesQueryKey = (slug: string) => {
  return [`/api/teacher/courses/${slug}/messages`] as const;
};

export const getListMyCourseMessagesQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyCourseMessages>>,
  TError = ErrorType<void>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyCourseMessages>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListMyCourseMessagesQueryKey(slug);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyCourseMessages>>
  > = ({ signal }) => listMyCourseMessages(slug, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyCourseMessages>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyCourseMessagesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyCourseMessages>>
>;
export type ListMyCourseMessagesQueryError = ErrorType<void>;

/**
 * @summary List messages you sent to a course you own
 */

export function useListMyCourseMessages<
  TData = Awaited<ReturnType<typeof listMyCourseMessages>>,
  TError = ErrorType<void>,
>(
  slug: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyCourseMessages>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyCourseMessagesQueryOptions(slug, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Send a broadcast message to all students enrolled in a course you own
 */
export const getBroadcastCourseMessageUrl = (slug: string) => {
  return `/api/teacher/courses/${slug}/messages`;
};

export const broadcastCourseMessage = async (
  slug: string,
  broadcastMessageInput: BroadcastMessageInput,
  options?: RequestInit,
): Promise<Message> => {
  return customFetch<Message>(getBroadcastCourseMessageUrl(slug), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(broadcastMessageInput),
  });
};

export const getBroadcastCourseMessageMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof broadcastCourseMessage>>,
    TError,
    { slug: string; data: BodyType<BroadcastMessageInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof broadcastCourseMessage>>,
  TError,
  { slug: string; data: BodyType<BroadcastMessageInput> },
  TContext
> => {
  const mutationKey = ["broadcastCourseMessage"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof broadcastCourseMessage>>,
    { slug: string; data: BodyType<BroadcastMessageInput> }
  > = (props) => {
    const { slug, data } = props ?? {};

    return broadcastCourseMessage(slug, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type BroadcastCourseMessageMutationResult = NonNullable<
  Awaited<ReturnType<typeof broadcastCourseMessage>>
>;
export type BroadcastCourseMessageMutationBody =
  BodyType<BroadcastMessageInput>;
export type BroadcastCourseMessageMutationError = ErrorType<void>;

/**
 * @summary Send a broadcast message to all students enrolled in a course you own
 */
export const useBroadcastCourseMessage = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof broadcastCourseMessage>>,
    TError,
    { slug: string; data: BodyType<BroadcastMessageInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof broadcastCourseMessage>>,
  TError,
  { slug: string; data: BodyType<BroadcastMessageInput> },
  TContext
> => {
  return useMutation(getBroadcastCourseMessageMutationOptions(options));
};

/**
 * @summary Add a lesson to a course you own
 */
export const getCreateMyLessonUrl = (slug: string) => {
  return `/api/teacher/courses/${slug}/lessons`;
};

export const createMyLesson = async (
  slug: string,
  createLessonInput: CreateLessonInput,
  options?: RequestInit,
): Promise<Lesson> => {
  return customFetch<Lesson>(getCreateMyLessonUrl(slug), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createLessonInput),
  });
};

export const getCreateMyLessonMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyLesson>>,
    TError,
    { slug: string; data: BodyType<CreateLessonInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMyLesson>>,
  TError,
  { slug: string; data: BodyType<CreateLessonInput> },
  TContext
> => {
  const mutationKey = ["createMyLesson"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMyLesson>>,
    { slug: string; data: BodyType<CreateLessonInput> }
  > = (props) => {
    const { slug, data } = props ?? {};

    return createMyLesson(slug, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMyLessonMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMyLesson>>
>;
export type CreateMyLessonMutationBody = BodyType<CreateLessonInput>;
export type CreateMyLessonMutationError = ErrorType<void>;

/**
 * @summary Add a lesson to a course you own
 */
export const useCreateMyLesson = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyLesson>>,
    TError,
    { slug: string; data: BodyType<CreateLessonInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createMyLesson>>,
  TError,
  { slug: string; data: BodyType<CreateLessonInput> },
  TContext
> => {
  return useMutation(getCreateMyLessonMutationOptions(options));
};

/**
 * @summary Reorder lessons in a course you own (assigns 1-based orderIndex by array position)
 */
export const getReorderMyCourseLessonsUrl = (slug: string) => {
  return `/api/teacher/courses/${slug}/lessons/reorder`;
};

export const reorderMyCourseLessons = async (
  slug: string,
  reorderInput: ReorderInput,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getReorderMyCourseLessonsUrl(slug), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(reorderInput),
  });
};

export const getReorderMyCourseLessonsMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reorderMyCourseLessons>>,
    TError,
    { slug: string; data: BodyType<ReorderInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof reorderMyCourseLessons>>,
  TError,
  { slug: string; data: BodyType<ReorderInput> },
  TContext
> => {
  const mutationKey = ["reorderMyCourseLessons"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof reorderMyCourseLessons>>,
    { slug: string; data: BodyType<ReorderInput> }
  > = (props) => {
    const { slug, data } = props ?? {};

    return reorderMyCourseLessons(slug, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReorderMyCourseLessonsMutationResult = NonNullable<
  Awaited<ReturnType<typeof reorderMyCourseLessons>>
>;
export type ReorderMyCourseLessonsMutationBody = BodyType<ReorderInput>;
export type ReorderMyCourseLessonsMutationError = ErrorType<void>;

/**
 * @summary Reorder lessons in a course you own (assigns 1-based orderIndex by array position)
 */
export const useReorderMyCourseLessons = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reorderMyCourseLessons>>,
    TError,
    { slug: string; data: BodyType<ReorderInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof reorderMyCourseLessons>>,
  TError,
  { slug: string; data: BodyType<ReorderInput> },
  TContext
> => {
  return useMutation(getReorderMyCourseLessonsMutationOptions(options));
};

/**
 * @summary Reorder readings in a lesson you own (assigns 0-based orderIndex by array position)
 */
export const getReorderMyLessonReadingsUrl = (id: number) => {
  return `/api/teacher/lessons/${id}/readings/reorder`;
};

export const reorderMyLessonReadings = async (
  id: number,
  reorderInput: ReorderInput,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getReorderMyLessonReadingsUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(reorderInput),
  });
};

export const getReorderMyLessonReadingsMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reorderMyLessonReadings>>,
    TError,
    { id: number; data: BodyType<ReorderInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof reorderMyLessonReadings>>,
  TError,
  { id: number; data: BodyType<ReorderInput> },
  TContext
> => {
  const mutationKey = ["reorderMyLessonReadings"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof reorderMyLessonReadings>>,
    { id: number; data: BodyType<ReorderInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return reorderMyLessonReadings(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReorderMyLessonReadingsMutationResult = NonNullable<
  Awaited<ReturnType<typeof reorderMyLessonReadings>>
>;
export type ReorderMyLessonReadingsMutationBody = BodyType<ReorderInput>;
export type ReorderMyLessonReadingsMutationError = ErrorType<void>;

/**
 * @summary Reorder readings in a lesson you own (assigns 0-based orderIndex by array position)
 */
export const useReorderMyLessonReadings = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reorderMyLessonReadings>>,
    TError,
    { id: number; data: BodyType<ReorderInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof reorderMyLessonReadings>>,
  TError,
  { id: number; data: BodyType<ReorderInput> },
  TContext
> => {
  return useMutation(getReorderMyLessonReadingsMutationOptions(options));
};

/**
 * @summary Reorder homework items in a lesson you own (assigns 0-based orderIndex by array position)
 */
export const getReorderMyLessonHomeworkUrl = (id: number) => {
  return `/api/teacher/lessons/${id}/homework/reorder`;
};

export const reorderMyLessonHomework = async (
  id: number,
  reorderInput: ReorderInput,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getReorderMyLessonHomeworkUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(reorderInput),
  });
};

export const getReorderMyLessonHomeworkMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reorderMyLessonHomework>>,
    TError,
    { id: number; data: BodyType<ReorderInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof reorderMyLessonHomework>>,
  TError,
  { id: number; data: BodyType<ReorderInput> },
  TContext
> => {
  const mutationKey = ["reorderMyLessonHomework"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof reorderMyLessonHomework>>,
    { id: number; data: BodyType<ReorderInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return reorderMyLessonHomework(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReorderMyLessonHomeworkMutationResult = NonNullable<
  Awaited<ReturnType<typeof reorderMyLessonHomework>>
>;
export type ReorderMyLessonHomeworkMutationBody = BodyType<ReorderInput>;
export type ReorderMyLessonHomeworkMutationError = ErrorType<void>;

/**
 * @summary Reorder homework items in a lesson you own (assigns 0-based orderIndex by array position)
 */
export const useReorderMyLessonHomework = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reorderMyLessonHomework>>,
    TError,
    { id: number; data: BodyType<ReorderInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof reorderMyLessonHomework>>,
  TError,
  { id: number; data: BodyType<ReorderInput> },
  TContext
> => {
  return useMutation(getReorderMyLessonHomeworkMutationOptions(options));
};

/**
 * @summary Update fields on a lesson belonging to a course you own
 */
export const getUpdateMyLessonUrl = (id: number) => {
  return `/api/teacher/lessons/${id}`;
};

export const updateMyLesson = async (
  id: number,
  updateLessonInput: UpdateLessonInput,
  options?: RequestInit,
): Promise<Lesson> => {
  return customFetch<Lesson>(getUpdateMyLessonUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateLessonInput),
  });
};

export const getUpdateMyLessonMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMyLesson>>,
    TError,
    { id: number; data: BodyType<UpdateLessonInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMyLesson>>,
  TError,
  { id: number; data: BodyType<UpdateLessonInput> },
  TContext
> => {
  const mutationKey = ["updateMyLesson"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMyLesson>>,
    { id: number; data: BodyType<UpdateLessonInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateMyLesson(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMyLessonMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMyLesson>>
>;
export type UpdateMyLessonMutationBody = BodyType<UpdateLessonInput>;
export type UpdateMyLessonMutationError = ErrorType<void>;

/**
 * @summary Update fields on a lesson belonging to a course you own
 */
export const useUpdateMyLesson = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMyLesson>>,
    TError,
    { id: number; data: BodyType<UpdateLessonInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateMyLesson>>,
  TError,
  { id: number; data: BodyType<UpdateLessonInput> },
  TContext
> => {
  return useMutation(getUpdateMyLessonMutationOptions(options));
};

/**
 * @summary Delete a lesson on a course you own (cascades to its readings and homework)
 */
export const getDeleteMyLessonUrl = (id: number) => {
  return `/api/teacher/lessons/${id}`;
};

export const deleteMyLesson = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteMyLessonUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteMyLessonMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyLesson>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteMyLesson>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteMyLesson"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteMyLesson>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteMyLesson(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteMyLessonMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteMyLesson>>
>;

export type DeleteMyLessonMutationError = ErrorType<void>;

/**
 * @summary Delete a lesson on a course you own (cascades to its readings and homework)
 */
export const useDeleteMyLesson = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyLesson>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteMyLesson>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteMyLessonMutationOptions(options));
};

/**
 * @summary Mark a live lesson's Zoom session as currently running so enrolled students see a Join CTA
 */
export const getStartLessonLiveUrl = (id: number) => {
  return `/api/teacher/lessons/${id}/live/start`;
};

export const startLessonLive = async (
  id: number,
  options?: RequestInit,
): Promise<Lesson> => {
  return customFetch<Lesson>(getStartLessonLiveUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getStartLessonLiveMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof startLessonLive>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof startLessonLive>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["startLessonLive"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof startLessonLive>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return startLessonLive(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StartLessonLiveMutationResult = NonNullable<
  Awaited<ReturnType<typeof startLessonLive>>
>;

export type StartLessonLiveMutationError = ErrorType<void>;

/**
 * @summary Mark a live lesson's Zoom session as currently running so enrolled students see a Join CTA
 */
export const useStartLessonLive = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof startLessonLive>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof startLessonLive>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getStartLessonLiveMutationOptions(options));
};

/**
 * @summary Clear the live indicator for a lesson when the Zoom session ends
 */
export const getStopLessonLiveUrl = (id: number) => {
  return `/api/teacher/lessons/${id}/live/stop`;
};

export const stopLessonLive = async (
  id: number,
  options?: RequestInit,
): Promise<Lesson> => {
  return customFetch<Lesson>(getStopLessonLiveUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getStopLessonLiveMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof stopLessonLive>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof stopLessonLive>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["stopLessonLive"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof stopLessonLive>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return stopLessonLive(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StopLessonLiveMutationResult = NonNullable<
  Awaited<ReturnType<typeof stopLessonLive>>
>;

export type StopLessonLiveMutationError = ErrorType<void>;

/**
 * @summary Clear the live indicator for a lesson when the Zoom session ends
 */
export const useStopLessonLive = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof stopLessonLive>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof stopLessonLive>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getStopLessonLiveMutationOptions(options));
};

/**
 * @summary List readings for a lesson on a course you own
 */
export const getListMyLessonReadingsUrl = (id: number) => {
  return `/api/teacher/lessons/${id}/readings`;
};

export const listMyLessonReadings = async (
  id: number,
  options?: RequestInit,
): Promise<Reading[]> => {
  return customFetch<Reading[]>(getListMyLessonReadingsUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getListMyLessonReadingsQueryKey = (id: number) => {
  return [`/api/teacher/lessons/${id}/readings`] as const;
};

export const getListMyLessonReadingsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyLessonReadings>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyLessonReadings>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListMyLessonReadingsQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyLessonReadings>>
  > = ({ signal }) => listMyLessonReadings(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyLessonReadings>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyLessonReadingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyLessonReadings>>
>;
export type ListMyLessonReadingsQueryError = ErrorType<void>;

/**
 * @summary List readings for a lesson on a course you own
 */

export function useListMyLessonReadings<
  TData = Awaited<ReturnType<typeof listMyLessonReadings>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyLessonReadings>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyLessonReadingsQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Add a reading to a lesson on a course you own
 */
export const getCreateMyLessonReadingUrl = (id: number) => {
  return `/api/teacher/lessons/${id}/readings`;
};

export const createMyLessonReading = async (
  id: number,
  createReadingInput: CreateReadingInput,
  options?: RequestInit,
): Promise<Reading> => {
  return customFetch<Reading>(getCreateMyLessonReadingUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createReadingInput),
  });
};

export const getCreateMyLessonReadingMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyLessonReading>>,
    TError,
    { id: number; data: BodyType<CreateReadingInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMyLessonReading>>,
  TError,
  { id: number; data: BodyType<CreateReadingInput> },
  TContext
> => {
  const mutationKey = ["createMyLessonReading"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMyLessonReading>>,
    { id: number; data: BodyType<CreateReadingInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return createMyLessonReading(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMyLessonReadingMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMyLessonReading>>
>;
export type CreateMyLessonReadingMutationBody = BodyType<CreateReadingInput>;
export type CreateMyLessonReadingMutationError = ErrorType<void>;

/**
 * @summary Add a reading to a lesson on a course you own
 */
export const useCreateMyLessonReading = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyLessonReading>>,
    TError,
    { id: number; data: BodyType<CreateReadingInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createMyLessonReading>>,
  TError,
  { id: number; data: BodyType<CreateReadingInput> },
  TContext
> => {
  return useMutation(getCreateMyLessonReadingMutationOptions(options));
};

/**
 * @summary Update a reading on a lesson belonging to a course you own
 */
export const getUpdateMyReadingUrl = (id: number) => {
  return `/api/teacher/readings/${id}`;
};

export const updateMyReading = async (
  id: number,
  updateReadingInput: UpdateReadingInput,
  options?: RequestInit,
): Promise<Reading> => {
  return customFetch<Reading>(getUpdateMyReadingUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateReadingInput),
  });
};

export const getUpdateMyReadingMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMyReading>>,
    TError,
    { id: number; data: BodyType<UpdateReadingInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMyReading>>,
  TError,
  { id: number; data: BodyType<UpdateReadingInput> },
  TContext
> => {
  const mutationKey = ["updateMyReading"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMyReading>>,
    { id: number; data: BodyType<UpdateReadingInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateMyReading(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMyReadingMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMyReading>>
>;
export type UpdateMyReadingMutationBody = BodyType<UpdateReadingInput>;
export type UpdateMyReadingMutationError = ErrorType<void>;

/**
 * @summary Update a reading on a lesson belonging to a course you own
 */
export const useUpdateMyReading = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMyReading>>,
    TError,
    { id: number; data: BodyType<UpdateReadingInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateMyReading>>,
  TError,
  { id: number; data: BodyType<UpdateReadingInput> },
  TContext
> => {
  return useMutation(getUpdateMyReadingMutationOptions(options));
};

/**
 * @summary Delete a reading on a lesson belonging to a course you own
 */
export const getDeleteMyReadingUrl = (id: number) => {
  return `/api/teacher/readings/${id}`;
};

export const deleteMyReading = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteMyReadingUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteMyReadingMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyReading>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteMyReading>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteMyReading"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteMyReading>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteMyReading(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteMyReadingMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteMyReading>>
>;

export type DeleteMyReadingMutationError = ErrorType<void>;

/**
 * @summary Delete a reading on a lesson belonging to a course you own
 */
export const useDeleteMyReading = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyReading>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteMyReading>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteMyReadingMutationOptions(options));
};

/**
 * @summary List homework items for a lesson on a course you own
 */
export const getListMyLessonHomeworkUrl = (id: number) => {
  return `/api/teacher/lessons/${id}/homework`;
};

export const listMyLessonHomework = async (
  id: number,
  options?: RequestInit,
): Promise<Homework[]> => {
  return customFetch<Homework[]>(getListMyLessonHomeworkUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getListMyLessonHomeworkQueryKey = (id: number) => {
  return [`/api/teacher/lessons/${id}/homework`] as const;
};

export const getListMyLessonHomeworkQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyLessonHomework>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyLessonHomework>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListMyLessonHomeworkQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyLessonHomework>>
  > = ({ signal }) => listMyLessonHomework(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyLessonHomework>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyLessonHomeworkQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyLessonHomework>>
>;
export type ListMyLessonHomeworkQueryError = ErrorType<void>;

/**
 * @summary List homework items for a lesson on a course you own
 */

export function useListMyLessonHomework<
  TData = Awaited<ReturnType<typeof listMyLessonHomework>>,
  TError = ErrorType<void>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listMyLessonHomework>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyLessonHomeworkQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Add a homework item to a lesson on a course you own
 */
export const getCreateMyLessonHomeworkUrl = (id: number) => {
  return `/api/teacher/lessons/${id}/homework`;
};

export const createMyLessonHomework = async (
  id: number,
  createHomeworkInput: CreateHomeworkInput,
  options?: RequestInit,
): Promise<Homework> => {
  return customFetch<Homework>(getCreateMyLessonHomeworkUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createHomeworkInput),
  });
};

export const getCreateMyLessonHomeworkMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyLessonHomework>>,
    TError,
    { id: number; data: BodyType<CreateHomeworkInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMyLessonHomework>>,
  TError,
  { id: number; data: BodyType<CreateHomeworkInput> },
  TContext
> => {
  const mutationKey = ["createMyLessonHomework"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMyLessonHomework>>,
    { id: number; data: BodyType<CreateHomeworkInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return createMyLessonHomework(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMyLessonHomeworkMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMyLessonHomework>>
>;
export type CreateMyLessonHomeworkMutationBody = BodyType<CreateHomeworkInput>;
export type CreateMyLessonHomeworkMutationError = ErrorType<void>;

/**
 * @summary Add a homework item to a lesson on a course you own
 */
export const useCreateMyLessonHomework = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyLessonHomework>>,
    TError,
    { id: number; data: BodyType<CreateHomeworkInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createMyLessonHomework>>,
  TError,
  { id: number; data: BodyType<CreateHomeworkInput> },
  TContext
> => {
  return useMutation(getCreateMyLessonHomeworkMutationOptions(options));
};

/**
 * @summary Update a homework item on a lesson belonging to a course you own
 */
export const getUpdateMyHomeworkUrl = (id: number) => {
  return `/api/teacher/homework/${id}`;
};

export const updateMyHomework = async (
  id: number,
  updateHomeworkInput: UpdateHomeworkInput,
  options?: RequestInit,
): Promise<Homework> => {
  return customFetch<Homework>(getUpdateMyHomeworkUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateHomeworkInput),
  });
};

export const getUpdateMyHomeworkMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMyHomework>>,
    TError,
    { id: number; data: BodyType<UpdateHomeworkInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMyHomework>>,
  TError,
  { id: number; data: BodyType<UpdateHomeworkInput> },
  TContext
> => {
  const mutationKey = ["updateMyHomework"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMyHomework>>,
    { id: number; data: BodyType<UpdateHomeworkInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateMyHomework(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMyHomeworkMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMyHomework>>
>;
export type UpdateMyHomeworkMutationBody = BodyType<UpdateHomeworkInput>;
export type UpdateMyHomeworkMutationError = ErrorType<void>;

/**
 * @summary Update a homework item on a lesson belonging to a course you own
 */
export const useUpdateMyHomework = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMyHomework>>,
    TError,
    { id: number; data: BodyType<UpdateHomeworkInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateMyHomework>>,
  TError,
  { id: number; data: BodyType<UpdateHomeworkInput> },
  TContext
> => {
  return useMutation(getUpdateMyHomeworkMutationOptions(options));
};

/**
 * @summary Delete a homework item on a lesson belonging to a course you own
 */
export const getDeleteMyHomeworkUrl = (id: number) => {
  return `/api/teacher/homework/${id}`;
};

export const deleteMyHomework = async (
  id: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteMyHomeworkUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteMyHomeworkMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyHomework>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteMyHomework>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deleteMyHomework"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteMyHomework>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deleteMyHomework(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteMyHomeworkMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteMyHomework>>
>;

export type DeleteMyHomeworkMutationError = ErrorType<void>;

/**
 * @summary Delete a homework item on a lesson belonging to a course you own
 */
export const useDeleteMyHomework = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyHomework>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteMyHomework>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeleteMyHomeworkMutationOptions(options));
};

/**
 * @summary Add a cohort section to a course you own
 */
export const getCreateMyCourseSectionUrl = (slug: string) => {
  return `/api/teacher/courses/${slug}/sections`;
};

export const createMyCourseSection = async (
  slug: string,
  createSectionInput: CreateSectionInput,
  options?: RequestInit,
): Promise<CourseSection> => {
  return customFetch<CourseSection>(getCreateMyCourseSectionUrl(slug), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createSectionInput),
  });
};

export const getCreateMyCourseSectionMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyCourseSection>>,
    TError,
    { slug: string; data: BodyType<CreateSectionInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createMyCourseSection>>,
  TError,
  { slug: string; data: BodyType<CreateSectionInput> },
  TContext
> => {
  const mutationKey = ["createMyCourseSection"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createMyCourseSection>>,
    { slug: string; data: BodyType<CreateSectionInput> }
  > = (props) => {
    const { slug, data } = props ?? {};

    return createMyCourseSection(slug, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateMyCourseSectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createMyCourseSection>>
>;
export type CreateMyCourseSectionMutationBody = BodyType<CreateSectionInput>;
export type CreateMyCourseSectionMutationError = ErrorType<void>;

/**
 * @summary Add a cohort section to a course you own
 */
export const useCreateMyCourseSection = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createMyCourseSection>>,
    TError,
    { slug: string; data: BodyType<CreateSectionInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createMyCourseSection>>,
  TError,
  { slug: string; data: BodyType<CreateSectionInput> },
  TContext
> => {
  return useMutation(getCreateMyCourseSectionMutationOptions(options));
};

/**
 * @summary Update a section on a course you own
 */
export const getUpdateMyCourseSectionUrl = (
  slug: string,
  sectionId: number,
) => {
  return `/api/teacher/courses/${slug}/sections/${sectionId}`;
};

export const updateMyCourseSection = async (
  slug: string,
  sectionId: number,
  updateSectionInput: UpdateSectionInput,
  options?: RequestInit,
): Promise<CourseSection> => {
  return customFetch<CourseSection>(
    getUpdateMyCourseSectionUrl(slug, sectionId),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(updateSectionInput),
    },
  );
};

export const getUpdateMyCourseSectionMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMyCourseSection>>,
    TError,
    { slug: string; sectionId: number; data: BodyType<UpdateSectionInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateMyCourseSection>>,
  TError,
  { slug: string; sectionId: number; data: BodyType<UpdateSectionInput> },
  TContext
> => {
  const mutationKey = ["updateMyCourseSection"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateMyCourseSection>>,
    { slug: string; sectionId: number; data: BodyType<UpdateSectionInput> }
  > = (props) => {
    const { slug, sectionId, data } = props ?? {};

    return updateMyCourseSection(slug, sectionId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateMyCourseSectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateMyCourseSection>>
>;
export type UpdateMyCourseSectionMutationBody = BodyType<UpdateSectionInput>;
export type UpdateMyCourseSectionMutationError = ErrorType<void>;

/**
 * @summary Update a section on a course you own
 */
export const useUpdateMyCourseSection = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateMyCourseSection>>,
    TError,
    { slug: string; sectionId: number; data: BodyType<UpdateSectionInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateMyCourseSection>>,
  TError,
  { slug: string; sectionId: number; data: BodyType<UpdateSectionInput> },
  TContext
> => {
  return useMutation(getUpdateMyCourseSectionMutationOptions(options));
};

/**
 * @summary Delete an empty section on a course you own
 */
export const getDeleteMyCourseSectionUrl = (
  slug: string,
  sectionId: number,
) => {
  return `/api/teacher/courses/${slug}/sections/${sectionId}`;
};

export const deleteMyCourseSection = async (
  slug: string,
  sectionId: number,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeleteMyCourseSectionUrl(slug, sectionId), {
    ...options,
    method: "DELETE",
  });
};

export const getDeleteMyCourseSectionMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyCourseSection>>,
    TError,
    { slug: string; sectionId: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteMyCourseSection>>,
  TError,
  { slug: string; sectionId: number },
  TContext
> => {
  const mutationKey = ["deleteMyCourseSection"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteMyCourseSection>>,
    { slug: string; sectionId: number }
  > = (props) => {
    const { slug, sectionId } = props ?? {};

    return deleteMyCourseSection(slug, sectionId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteMyCourseSectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteMyCourseSection>>
>;

export type DeleteMyCourseSectionMutationError = ErrorType<void>;

/**
 * @summary Delete an empty section on a course you own
 */
export const useDeleteMyCourseSection = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMyCourseSection>>,
    TError,
    { slug: string; sectionId: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteMyCourseSection>>,
  TError,
  { slug: string; sectionId: number },
  TContext
> => {
  return useMutation(getDeleteMyCourseSectionMutationOptions(options));
};

/**
 * @summary Set the zoom schedule for one lesson in one section
 */
export const getUpsertMySectionLessonSessionUrl = (
  sectionId: number,
  lessonId: number,
) => {
  return `/api/teacher/sections/${sectionId}/lessons/${lessonId}/session`;
};

export const upsertMySectionLessonSession = async (
  sectionId: number,
  lessonId: number,
  upsertLessonSessionInput: UpsertLessonSessionInput,
  options?: RequestInit,
): Promise<SectionSession> => {
  return customFetch<SectionSession>(
    getUpsertMySectionLessonSessionUrl(sectionId, lessonId),
    {
      ...options,
      method: "PUT",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(upsertLessonSessionInput),
    },
  );
};

export const getUpsertMySectionLessonSessionMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof upsertMySectionLessonSession>>,
    TError,
    {
      sectionId: number;
      lessonId: number;
      data: BodyType<UpsertLessonSessionInput>;
    },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof upsertMySectionLessonSession>>,
  TError,
  {
    sectionId: number;
    lessonId: number;
    data: BodyType<UpsertLessonSessionInput>;
  },
  TContext
> => {
  const mutationKey = ["upsertMySectionLessonSession"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof upsertMySectionLessonSession>>,
    {
      sectionId: number;
      lessonId: number;
      data: BodyType<UpsertLessonSessionInput>;
    }
  > = (props) => {
    const { sectionId, lessonId, data } = props ?? {};

    return upsertMySectionLessonSession(
      sectionId,
      lessonId,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type UpsertMySectionLessonSessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof upsertMySectionLessonSession>>
>;
export type UpsertMySectionLessonSessionMutationBody =
  BodyType<UpsertLessonSessionInput>;
export type UpsertMySectionLessonSessionMutationError = ErrorType<void>;

/**
 * @summary Set the zoom schedule for one lesson in one section
 */
export const useUpsertMySectionLessonSession = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof upsertMySectionLessonSession>>,
    TError,
    {
      sectionId: number;
      lessonId: number;
      data: BodyType<UpsertLessonSessionInput>;
    },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof upsertMySectionLessonSession>>,
  TError,
  {
    sectionId: number;
    lessonId: number;
    data: BodyType<UpsertLessonSessionInput>;
  },
  TContext
> => {
  return useMutation(getUpsertMySectionLessonSessionMutationOptions(options));
};

/**
 * @summary Mark a section's Zoom session as currently running
 */
export const getStartSectionLessonLiveUrl = (
  sectionId: number,
  lessonId: number,
) => {
  return `/api/teacher/sections/${sectionId}/lessons/${lessonId}/live/start`;
};

export const startSectionLessonLive = async (
  sectionId: number,
  lessonId: number,
  options?: RequestInit,
): Promise<SectionSession> => {
  return customFetch<SectionSession>(
    getStartSectionLessonLiveUrl(sectionId, lessonId),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getStartSectionLessonLiveMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof startSectionLessonLive>>,
    TError,
    { sectionId: number; lessonId: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof startSectionLessonLive>>,
  TError,
  { sectionId: number; lessonId: number },
  TContext
> => {
  const mutationKey = ["startSectionLessonLive"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof startSectionLessonLive>>,
    { sectionId: number; lessonId: number }
  > = (props) => {
    const { sectionId, lessonId } = props ?? {};

    return startSectionLessonLive(sectionId, lessonId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StartSectionLessonLiveMutationResult = NonNullable<
  Awaited<ReturnType<typeof startSectionLessonLive>>
>;

export type StartSectionLessonLiveMutationError = ErrorType<void>;

/**
 * @summary Mark a section's Zoom session as currently running
 */
export const useStartSectionLessonLive = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof startSectionLessonLive>>,
    TError,
    { sectionId: number; lessonId: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof startSectionLessonLive>>,
  TError,
  { sectionId: number; lessonId: number },
  TContext
> => {
  return useMutation(getStartSectionLessonLiveMutationOptions(options));
};

/**
 * @summary Clear the live indicator for a section's Zoom session
 */
export const getStopSectionLessonLiveUrl = (
  sectionId: number,
  lessonId: number,
) => {
  return `/api/teacher/sections/${sectionId}/lessons/${lessonId}/live/stop`;
};

export const stopSectionLessonLive = async (
  sectionId: number,
  lessonId: number,
  options?: RequestInit,
): Promise<SectionSession> => {
  return customFetch<SectionSession>(
    getStopSectionLessonLiveUrl(sectionId, lessonId),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getStopSectionLessonLiveMutationOptions = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof stopSectionLessonLive>>,
    TError,
    { sectionId: number; lessonId: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof stopSectionLessonLive>>,
  TError,
  { sectionId: number; lessonId: number },
  TContext
> => {
  const mutationKey = ["stopSectionLessonLive"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof stopSectionLessonLive>>,
    { sectionId: number; lessonId: number }
  > = (props) => {
    const { sectionId, lessonId } = props ?? {};

    return stopSectionLessonLive(sectionId, lessonId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StopSectionLessonLiveMutationResult = NonNullable<
  Awaited<ReturnType<typeof stopSectionLessonLive>>
>;

export type StopSectionLessonLiveMutationError = ErrorType<void>;

/**
 * @summary Clear the live indicator for a section's Zoom session
 */
export const useStopSectionLessonLive = <
  TError = ErrorType<void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof stopSectionLessonLive>>,
    TError,
    { sectionId: number; lessonId: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof stopSectionLessonLive>>,
  TError,
  { sectionId: number; lessonId: number },
  TContext
> => {
  return useMutation(getStopSectionLessonLiveMutationOptions(options));
};

/**
 * @summary Messages for courses the signed-in user is enrolled in (auth required)
 */
export const getListMyMessagesUrl = () => {
  return `/api/messages/me`;
};

export const listMyMessages = async (
  options?: RequestInit,
): Promise<MessageWithCourse[]> => {
  return customFetch<MessageWithCourse[]>(getListMyMessagesUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListMyMessagesQueryKey = () => {
  return [`/api/messages/me`] as const;
};

export const getListMyMessagesQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyMessages>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyMessages>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMyMessagesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyMessages>>> = ({
    signal,
  }) => listMyMessages({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyMessages>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyMessagesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyMessages>>
>;
export type ListMyMessagesQueryError = ErrorType<void>;

/**
 * @summary Messages for courses the signed-in user is enrolled in (auth required)
 */

export function useListMyMessages<
  TData = Awaited<ReturnType<typeof listMyMessages>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyMessages>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyMessagesQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Direct messages teachers have sent to the signed-in student
 */
export const getListMyDirectMessagesUrl = () => {
  return `/api/messages/me/direct`;
};

export const listMyDirectMessages = async (
  options?: RequestInit,
): Promise<DirectMessageWithCourse[]> => {
  return customFetch<DirectMessageWithCourse[]>(getListMyDirectMessagesUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListMyDirectMessagesQueryKey = () => {
  return [`/api/messages/me/direct`] as const;
};

export const getListMyDirectMessagesQueryOptions = <
  TData = Awaited<ReturnType<typeof listMyDirectMessages>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyDirectMessages>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMyDirectMessagesQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listMyDirectMessages>>
  > = ({ signal }) => listMyDirectMessages({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listMyDirectMessages>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListMyDirectMessagesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMyDirectMessages>>
>;
export type ListMyDirectMessagesQueryError = ErrorType<void>;

/**
 * @summary Direct messages teachers have sent to the signed-in student
 */

export function useListMyDirectMessages<
  TData = Awaited<ReturnType<typeof listMyDirectMessages>>,
  TError = ErrorType<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listMyDirectMessages>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListMyDirectMessagesQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Aggregate counts for the homepage
 */
export const getGetStatsOverviewUrl = () => {
  return `/api/stats/overview`;
};

export const getStatsOverview = async (
  options?: RequestInit,
): Promise<StatsOverview> => {
  return customFetch<StatsOverview>(getGetStatsOverviewUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetStatsOverviewQueryKey = () => {
  return [`/api/stats/overview`] as const;
};

export const getGetStatsOverviewQueryOptions = <
  TData = Awaited<ReturnType<typeof getStatsOverview>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getStatsOverview>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetStatsOverviewQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getStatsOverview>>
  > = ({ signal }) => getStatsOverview({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getStatsOverview>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetStatsOverviewQueryResult = NonNullable<
  Awaited<ReturnType<typeof getStatsOverview>>
>;
export type GetStatsOverviewQueryError = ErrorType<unknown>;

/**
 * @summary Aggregate counts for the homepage
 */

export function useGetStatsOverview<
  TData = Awaited<ReturnType<typeof getStatsOverview>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getStatsOverview>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetStatsOverviewQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Next upcoming live zoom sessions across all courses
 */
export const getListUpcomingSessionsUrl = (
  params?: ListUpcomingSessionsParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/stats/upcoming-sessions?${stringifiedParams}`
    : `/api/stats/upcoming-sessions`;
};

export const listUpcomingSessions = async (
  params?: ListUpcomingSessionsParams,
  options?: RequestInit,
): Promise<UpcomingSession[]> => {
  return customFetch<UpcomingSession[]>(getListUpcomingSessionsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListUpcomingSessionsQueryKey = (
  params?: ListUpcomingSessionsParams,
) => {
  return [`/api/stats/upcoming-sessions`, ...(params ? [params] : [])] as const;
};

export const getListUpcomingSessionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listUpcomingSessions>>,
  TError = ErrorType<unknown>,
>(
  params?: ListUpcomingSessionsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listUpcomingSessions>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListUpcomingSessionsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listUpcomingSessions>>
  > = ({ signal }) =>
    listUpcomingSessions(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listUpcomingSessions>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListUpcomingSessionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listUpcomingSessions>>
>;
export type ListUpcomingSessionsQueryError = ErrorType<unknown>;

/**
 * @summary Next upcoming live zoom sessions across all courses
 */

export function useListUpcomingSessions<
  TData = Awaited<ReturnType<typeof listUpcomingSessions>>,
  TError = ErrorType<unknown>,
>(
  params?: ListUpcomingSessionsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listUpcomingSessions>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListUpcomingSessionsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}
